/**
 * Partner branding is data from the partner's public profile. Validate it
 * before it reaches a style attribute, and pick readable text for it.
 */
export const FALLBACK_BRAND = '#1f2937';
export function safeBrandColor(value) {
    return typeof value === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim()) ? value.trim() : FALLBACK_BRAND;
}
function hexToRgb(hex) {
    let h = hex.replace('#', '');
    if (h.length === 3)
        h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** WCAG relative luminance. */
function luminance([r, g, b]) {
    const lin = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
/** White or near-black text, whichever contrasts more with the brand colour. */
export function contrastText(hex) {
    const l = luminance(hexToRgb(safeBrandColor(hex)));
    const onWhite = 1.05 / (l + 0.05);
    const onDark = (l + 0.05) / 0.05;
    return onWhite >= onDark ? '#ffffff' : '#111827';
}
/** Only https (or same-origin relative) logos are rendered. */
export function safeLogoUrl(value) {
    if (typeof value !== 'string' || !value.trim())
        return null;
    const v = value.trim();
    if (v.startsWith('/') && !v.startsWith('//'))
        return v;
    try {
        return new URL(v).protocol === 'https:' ? v : null;
    }
    catch {
        return null;
    }
}
/**
 * The CSS custom properties every desk primitive reads for the program colour.
 * Spread onto the element that wraps the desk: `<div style={brandStyle(color)}>`.
 */
export function brandStyle(brandColor) {
    const brand = safeBrandColor(brandColor);
    return { '--pn-brand': brand, '--pn-on-brand': contrastText(brand) };
}
export function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '?';
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
    return (first + last).toUpperCase();
}
